import React, { useMemo } from 'react';

import DeleteConfirmationDialog from '../../DeleteConfirmationDialog';
import ImagesByRoom from '../../ImagesVideo/ImagesByRoom';
import type { PropertyRoomTypeWithImages } from '../../ImagesVideo/types';
import SelectionDeleteBar from '../../SelectionDeleteBar';
import useBulkImageSelection from '../../useBulkImageSelection';
import type { OfferBuilderMediaController } from '../../useOfferBuilderMedia';

interface Props {
  media: OfferBuilderMediaController;
  onOpenImagesVideo: () => void;
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>;
  roomKey: string;
}

export default function ImagesTab({ media, onOpenImagesVideo, propertyRoomTypes, roomKey }: Props) {
  const displayRoomImageOrderOverrides = useMemo(
    () => ({ ...media.savedRoomImageOrders, ...media.roomImageOrderOverrides }),
    [media.roomImageOrderOverrides, media.savedRoomImageOrders],
  );
  const roomImages = useMemo(
    () => media.mediaImages.filter((image) => image.sourceType === 'room' && image.sourceRoomId === roomKey),
    [media.mediaImages, roomKey],
  );
  const {
    bulkDeleteDialogBody,
    isBulkDeleteDialogOpen,
    isBulkDeleting,
    selectedImagePublicIds,
    clearSelection,
    closeBulkDeleteDialog,
    confirmBulkDelete,
    openBulkDeleteDialog,
    toggleSelected,
  } = useBulkImageSelection({ deleteScopeLabel: 'this room', media, selectableImages: roomImages });

  return (
    <>
      <SelectionDeleteBar
        selectedCount={selectedImagePublicIds.length}
        onClear={clearSelection}
        onDeleteSelected={openBulkDeleteDialog}
      />

      <ImagesByRoom
        hiddenImagePublicIds={media.hiddenImagePublicIds}
        imagePublicIdReplacements={media.imagePublicIdReplacements}
        isMetadataEditable
        mediaImages={media.mediaImages}
        selectedImagePublicIds={selectedImagePublicIds}
        onEditImage={media.editRoomImage}
        onOpenImagesVideo={onOpenImagesVideo}
        onRemoveImageFromRoom={media.removeImageFromRoom}
        onReorderRoomImages={media.reorderRoomImages}
        onToggleHiddenImage={media.toggleHiddenImage}
        onToggleSelected={toggleSelected}
        onUpdateImageTitle={media.updateImageTitle}
        onUploadImagesToRoom={media.uploadFilesToRoom}
        propertyRoomTypes={propertyRoomTypes}
        removedRoomImageIdsByRoomKey={media.removedRoomImageIdsByRoomKey}
        roomImageOrderOverrides={displayRoomImageOrderOverrides}
        roomImageOrderResetKey={media.roomImageOrderResetKey}
        showHeader={false}
        showRoomDetails={false}
        uploadedRoomAssignments={media.uploadedRoomAssignments}
        visibleRoomKeys={[roomKey]}
      />

      <DeleteConfirmationDialog
        body={bulkDeleteDialogBody}
        isConfirming={isBulkDeleting}
        open={isBulkDeleteDialogOpen}
        title="Delete selected images?"
        onClose={closeBulkDeleteDialog}
        onConfirm={confirmBulkDelete}
      />
    </>
  );
}
